import { useEffect, useRef, useState, type FC, type SVGProps } from "react";
import { trackButtonPressed } from "../../analytics/tracking";
import { HARNESS_OPTIMIZER_APP_FLAG, useFlags } from "../../services/flags";
import { useWsNavigate } from "../../workspaces";
import { HARNESS_OPTIMIZER_PATH } from "../../auth/paths";
import agentGatewayBg from "../../design-system/assets/apps/agent-gateway-bg.png";
import harnessOptimizerBg from "../../design-system/assets/apps/harness-optimizer-bg.png";
import AgentGatewayGlyphRaw from "../../design-system/assets/apps/agent-gateway-glyph.svg";
import HarnessOptimizerGlyphRaw from "../../design-system/assets/apps/harness-optimizer-glyph.svg";

// svgr turns these into components at runtime; vite/client types them as
// string (same cast as design-system/assets/logos).
type Glyph = FC<SVGProps<SVGSVGElement>>;
const AgentGatewayGlyph = AgentGatewayGlyphRaw as unknown as Glyph;
const HarnessOptimizerGlyph = HarnessOptimizerGlyphRaw as unknown as Glyph;

/* App switcher (OPSS-379), per the Figma design (node 10:1722): a 2×2 grid
 * button in the always-dark bar opening a #280611 panel — same family as the
 * profile menu — with one 70px row per app. Each row pairs the app name with
 * its 40px gradient tile. */
export function AppsMenu() {
  const wsNav = useWsNavigate();
  const { isEnabled } = useFlags();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  // Gated on the Harness Optimizer rollout flag: with a single sibling app
  // there is nothing to switch to. Off/unknown ⇒ hidden (fail-safe), so the
  // switcher only ever appears — never flashes on and retracts.
  if (!isEnabled(HARNESS_OPTIMIZER_APP_FLAG)) return null;

  return (
    <div className="relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Switch app"
        aria-haspopup="menu"
        aria-expanded={open}
        className="group flex h-[27px] w-[28px] items-center justify-center rounded-[5px] transition-colors hover:bg-white/10"
      >
        <span className="grid grid-cols-2 gap-[3.75px]">
          {Array.from({ length: 4 }, (_, i) => (
            <span
              key={i}
              className={`h-[5.625px] w-[5.625px] rounded-[1px] transition-colors group-hover:bg-[#fbfbfb] ${
                open ? "bg-[#fbfbfb]" : "bg-[rgba(153,136,142,0.5)]"
              }`}
            />
          ))}
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-[225px] bg-[#280611] shadow-[0_25px_25px_rgba(0,0,0,0.25)]"
          style={{ border: "1px solid rgba(255,255,255,0.07)" }}
        >
          <AppRow
            label="Agent Gateway"
            divider
            tile={
              <>
                <img alt="" src={agentGatewayBg} className={TILE_BG} />
                <AgentGatewayGlyph className="absolute left-[8px] top-[7px] h-[25px] w-[25px]" />
              </>
            }
            onSelect={() => {
              setOpen(false);
              wsNav("/");
            }}
          />
          <AppRow
            label="Harness Optimizer"
            tile={
              <>
                <img alt="" src={harnessOptimizerBg} className={TILE_BG} />
                <HarnessOptimizerGlyph className="absolute left-[7.35px] top-[7.84px] h-[30.32px] w-[25.65px]" />
              </>
            }
            onSelect={() => window.location.assign(HARNESS_OPTIMIZER_PATH)}
          />
        </div>
      )}
    </div>
  );
}

const TILE_BG =
  "absolute inset-0 size-full rounded-[5px] object-cover object-bottom";

function AppRow({
  label,
  tile,
  divider = false,
  onSelect,
}: {
  label: string;
  tile: React.ReactNode;
  divider?: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      role="menuitem"
      onClick={() => {
        trackButtonPressed(label, "app");
        onSelect();
      }}
      className="flex h-[70px] w-full items-center justify-between pl-[25px] pr-[20px] text-left text-[13px] text-[#99888e] transition-colors hover:bg-[#33121d] hover:text-[#fbfbfb]"
      style={
        divider
          ? { borderBottom: "1px solid rgba(255,255,255,0.08)" }
          : undefined
      }
    >
      {label}
      {/* The tile glyphs are exported with #FBFBFB fills; svgo rewrites them
       * to currentColor, so pin the color here, independent of row hover. */}
      <span className="relative h-[40px] w-[40px] flex-shrink-0 rounded-[5px] text-[#fbfbfb]">
        {tile}
      </span>
    </button>
  );
}
